import { AxiosResponse } from 'axios';
import client from 'api/client';
import { getActingProfileUserId } from 'utils/selectedAccount';

// In-flight dedupe: while a GET for a given candidate id is pending, concurrent
// callers share the same promise. Entries are deleted once the promise settles,
// so we do not cache results across mounts (TTL = 0).
const inFlightGetCompletedForms = new Map<number, Promise<AxiosResponse>>();

// Some callers compute `candidateID` as `Number(params.id)` or read it from
// Redux before the user is hydrated, which can yield NaN / 0 / undefined.
// Hitting the backend with those produces a 500 (invalid integer cast), so we
// short-circuit here and hand callers a `{ data: null }` response — their
// existing `resp?.data` checks then fall through without side effects.
const isValidId = (id: number): boolean => Number.isFinite(id) && id > 0;

const resolveCompletedFormsUserId = (id: number): number => {
    const actingId = getActingProfileUserId();
    return actingId ?? id;
};

export function getCompletedForms(id: number): Promise<AxiosResponse> {
    const userId = resolveCompletedFormsUserId(id);
    if (!isValidId(userId)) {
        return Promise.resolve({ data: null } as AxiosResponse);
    }

    const existing = inFlightGetCompletedForms.get(userId);
    if (existing) return existing;

    const request = client.get(`/candidate_info/${userId}/completed-forms`);
    const tracked = request.finally(() => {
        inFlightGetCompletedForms.delete(userId);
    });

    inFlightGetCompletedForms.set(userId, tracked);
    return tracked;
}

export function editCompletedFormStatus(id: number, payload: { [key: string]: boolean }) {
    const userId = resolveCompletedFormsUserId(id);
    if (!isValidId(userId)) {
        return Promise.resolve({ data: null } as AxiosResponse);
    }
    return client.patch(`/candidate_info/${userId}/completed-forms`, payload);
}
